<template>
  <div class="home-container">
    <DeviceSetupForm v-if="!deviceConfigured" />
    <h2 v-if="deviceConfigured" class="content-block">{{siteInfo.name}}</h2>

    <div class="content-block dx-card">
      <DxButton icon="video" text="Open Realtime Monitor" :width="250" :on-click="openRTMonitorClick"/>
      <DxButton icon="folder" text="Explore Site Dashboards" :width="250" :on-click="openDashFolderClick"/>
      <DxDropDownButton 
        text="Open Dashboard"
        icon="mergecells"
        styling-mode="contained"
        :width="250" 
        :items="dashboardsDataSource"
        key-expr="id"
        display-expr="title"
        @item-click="openDashboardClick"
      />
    </div>

    <div v-if="mapEnabled" class="dx-card content-block" style="height: 33vh; ">
      <!--l-map @update:zoom="zoomUpdate" @update:center="centerUpdate"></l-map-->
      <l-map
        v-model:zoom="mapZoom"
        v-model:center="mapCenterCoords"
      >
        <l-tile-layer :url="mapUrl" :attribution="mapAttribution" />
        <l-control position="bottomleft" >
          <button @click="updateAddressFromMap" :disabled="!isMapLocationEdited">Obtain Location</button>
          <button @click="mapCenterRestore" :disabled="isMapCenterEdited">Center Location</button>
        </l-control>

        <l-marker :lat-lng="mapTargetCoords" draggable @moveend="locatePoint">
          <l-icon :icon-size="[28, 45]" :icon-anchor="[14,45]" :popup-anchor="[1, -34]" >
            <img src="../assets/enfasys-marker.png" :height="45">
          </l-icon>
          <l-popup>{{mapAddress}}</l-popup>
        </l-marker>

        <l-marker v-for="(node, index) in mapNodeList" :key="'mapNode'+index" :lat-lng="[node.gpsLat, node.gpsLng]">
          <l-popup><strong>{{node.caption}}</strong> {{node.description}}</l-popup>
        </l-marker>

      </l-map>
      </div>
    <div v-if="mapEnabled" class="dx-card content-block kpi-container" style="margin-top: 0px;" >
      <small>* To edit the site location, move the marker in the map to the desired location and click update, then save the changes in the installation information tab below</small>
    </div>

    <div v-if="deviceConfigured" class="content-block">
      <DxTabs :items="tabs" selection-mode="single" v-model:selected-index="tabSelectedIx" />

      <div class="dx-card responsive-paddings" v-if="tabSelectedIx==0">
        <form @submit.prevent="updateSiteInfo">
          <DxForm :form-data="siteInfo" :col-count="3" :min-col-width="200" :show-validation-summary="true"> 
            <DxSimpleItem data-field="name" :col-span="3" :is-required="true" />
            <DxSimpleItem data-field="locationAddr" :col-span="2" :is-required="true" />
            <DxSimpleItem data-field="country_code" :is-required="true" editor-type="dxSelectBox" :editor-options="{ searchEnabled: true, dataSource: countriesDataSource, valueExpr: 'alpha2Code', displayExpr: 'name'}" />
            <DxSimpleItem data-field="locationLat" editor-type="dxNumberBox" :editor-options="{ disabled: false, max: 90, min: -90, step: 0.05 }" />
            <DxSimpleItem data-field="locationLong" editor-type="dxNumberBox" :editor-options="{ disabled: false, max: 180, min: -180, step: 0.05 }"/>
            <DxButtonItem :disabled="!isUserAdmin" :button-options="{ text: 'Update information', type:'success', preventDefault: true, useSubmitBehavior: true}"/>
          </DxForm>
        </form>
      </div>

      <div class="dx-card responsive-paddings" v-if="tabSelectedIx==1">
        <form  @submit.prevent="updateOrgInfo">
          <DxForm ref="configForm" col-count="3" :form-data="orgInfo" :show-validation-summary="true">
            <DxSimpleItem data-field="name" :col-span="3" :is-required="true" />
            <DxSimpleItem data-field="address" :col-span="2" />
            <DxSimpleItem data-field="country_code" :is-required="true" editor-type="dxSelectBox" :editor-options="{ searchEnabled: true, dataSource: countriesDataSource, valueExpr: 'alpha2Code', displayExpr: 'name'}" />
            <DxSimpleItem data-field="city" />
            <DxSimpleItem data-field="description" />
            <DxButtonItem :disabled="!isUserAdmin" :button-options="{ text: 'Update information', type:'success', useSubmitBehavior: true}"/>
          </DxForm>
        </form>
        <small>Use this form to update the general organization information (Only name and country are mandatory fields). You will need administrative org privileges to edit this information.</small>
      </div>
    </div>

  </div>
</template>

<script>
import DataService from '../services/DataServiceTemplate';
import RawDataService from '../services/RawDataServiceTemplate';
import * as GeoCodingDS from '../services/GeoCodingDataService';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { ref, reactive, watch, computed } from 'vue';
import DeviceSetupForm from '../components/device-setup.vue';
import DxTabs from "devextreme-vue/tabs";
import { DxForm, DxSimpleItem, DxButtonItem } from 'devextreme-vue/form';
import DxButton from 'devextreme-vue/button';
import DxDropDownButton from 'devextreme-vue/drop-down-button';

import notify from 'devextreme/ui/notify';

import {
  LMap,
  LControl,
  LTileLayer,
  LMarker,
  LPopup,
  LIcon,
} from "@vue-leaflet/vue-leaflet";
import "leaflet/dist/leaflet.css";

export default {
  components: {
    DeviceSetupForm,

    LMap,
    LControl,
    LTileLayer,
    LMarker,
    LPopup,
    LIcon,

    DxTabs,
    DxForm,
    DxSimpleItem,
    DxButtonItem,
    
    DxButton,
    DxDropDownButton,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const store = useStore();

    const siteId = ref(0);
    const siteInfo = reactive({
      id: 'local',
      name: "unloaded device",
      description: "",
      locationAddr: "",
      country_code: "",
      locationLat: "2021-01-01",
      locationLong: 0,
    });
    const orgInfo = reactive({
      id: "",
      name: "",
      description: null,
      address: "",
      city: "",
      country_code: "",
    })

    const deviceConfigured = ref(false);
    const countriesDataService = new RawDataService('masters/countries');
    const nodesDataService = new RawDataService('masters/enfyoGraphNodes');
    const orgDataService = new RawDataService('orgs');
    const sitesDataService = new DataService('/sites', 'id');

    const dashboardsDataSource = ref([]);
    const myDashboardsDataService = new RawDataService('/sites/local/dashboards')
    myDashboardsDataService.getAll()
      .then( data => { dashboardsDataSource.value = data } )
      .catch( err => { notify("Error loading dahsboards", "error", 3000); console.log(err) } )

    const countriesDataSource = ref([{name: 'Spain', alpha2Code: 'ES'}]);
    
    const mapEnabled = ref(false);
    const mapZoom = ref(11);
    const mapNodeList = ref([]);
    const mapCenterCoords = ref([43.52471, -5.629260]);
    const mapTargetCoords = ref([43.52473, -5.629262]);
    const mapUrl= ref('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png');
    const mapAttribution = ref('&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors & Enfasys S.L');
    const mapAddress = ref('move around to relocate your site and set an address');
    const mapCountry = ref('');
    const isMapLocationEdited = ref(false);
    //const isMapCenterEdited = ref(false);
    const isMapCenterEdited = computed(() => {
      const isLatEqual = compareRound( mapCenterCoords.value.lat??mapCenterCoords.value[0], mapTargetCoords.value[0] )
      const isLngEqual = compareRound( mapCenterCoords.value.lng??mapCenterCoords.value[1], mapTargetCoords.value[1] )
      return (isLatEqual&&isLngEqual)
    })
    
    const compareRound = (coords1, coords2) => {
      let round1 = Math.round(coords1*1000);
      let round2 = Math.round(coords2*1000);
      return round1==round2;
    }

    const tabSelectedIx = ref(0);
    const tabs = ref([
      { text: "Installation Information" },
      { text: "Organization Information" },
    ]);
    const isUserAdmin = store.getters.hasAdminAccess(100)//readonly - sourceuser

    //TODO: get permission to access dashboards?

    //map and site data refresh
    const updateSiteData = async () => {
      if(!siteId.value) return ; //invalid site id
      try{
        const { data: siteData } = await sitesDataService.dataStore.byKey(siteId.value);
        const nodeList = await nodesDataService.getAll();
        mapNodeList.value = nodeList.filter?.(node => node.enabled&&node.gpsLat&&node.gpsLng)||[];

        if(siteData?.name) deviceConfigured.value=true;
        Object.assign(siteInfo, siteData);
        mapCenterCoords.value=[siteData.locationLat, siteData.locationLong];
        mapTargetCoords.value=[siteData.locationLat, siteData.locationLong];
        mapZoom.value=13;
        mapEnabled.value=true;
      }
      catch(error){
        siteInfo.id = -1;
        console.log(error) 
        notify(error.message||"error loading site data", "error", 3000);
      }

    }

    countriesDataService.getAll()
      .then( (data) => { countriesDataSource.value = data; })
      .catch( error => { notify(error.message||"error loading site data", "error", 3000); });
    deviceConfigured.value=false;
    siteId.value = 'local';
    orgDataService.getByKey('local')
      .then( data => {
        Object.assign(orgInfo, data); 
      })
      .catch( err => { notify(err.message||"error getting organization data","error",5000); });
    updateSiteData();

    //necessary when opening the same view with different params
    watch(route, () => {
      siteId.value = route.params.siteId||'local';
      updateSiteData();
    })

    const updateAddressFromMap = () =>{
      siteInfo.locationAddr = mapAddress.value;
      siteInfo.country_name = mapCountry.value;
      siteInfo.locationLat = mapTargetCoords.value[0];
      siteInfo.locationLong = mapTargetCoords.value[1];
      notify("address and location changed, remember to save changes", "success", 3000);
    }
    const locatePoint = (evt) => {
      if (evt?.target?._latlng){
        const { lat, lng } = evt.target._latlng;
        mapTargetCoords.value=[lat, lng];
        GeoCodingDS.getAddressByCoords(lat, lng)
        .then( data => { 
          mapAddress.value = data.display_name || 'address not found'; 
          mapCountry.value = data.address? data.address.country||'' : '';
          isMapLocationEdited.value = true;
          //isMapCenterEdited.value=false;
        })
        .catch( error => { notify(error.message||"error loading address", "error", 3000);} );
      }
    }
    const updateSiteInfo = () => {
      sitesDataService.dataStore.update(siteInfo.id, siteInfo)
      .then(data => { notify(data.message||"site data sent", "success", 3000); })
      .catch( error => { notify(error.message||"error loading address", "error", 3000);} );
    }
    const updateOrgInfo = () => {
      orgDataService.update('local', orgInfo)
      .then(response => { notify(response.message||"org data sent", "success", 3000); })
      .catch( err => {notify(err.message||"error saving org data in server","error",3000);} )
    }
    const openDashFolderClick = () => {
      router.push({ name: 'dashboardnav', params: { uid: siteInfo.grafanaFolderUid, folderOrDash: 'f' } });
    }
    const openDashboardClick =(e) => {
      if (e.itemData){
        router.push({ name: 'dashboardnav', params: { url: e.itemData.url, uid: e.itemData.uid, folderOrDash: 'd' } });
      }
    }
    const openRTMonitorClick = () => {
      router.push({ name: 'realtime', params: { uid: 'local' } });
    }
    const mapCenterRestore = () => { 
      mapZoom.value = 13;
      mapCenterCoords.value = [ mapTargetCoords.value[0], mapTargetCoords.value[1]]; 
      //isMapCenterEdited.value = false;
    }
    
    return {
      siteInfo,
      orgInfo,

      mapZoom,
      mapAddress,
      mapEnabled,
      mapCenterCoords,
      mapTargetCoords,
      mapNodeList,
      mapUrl,
      mapAttribution,
      mapCenterRestore,
      updateAddressFromMap,
      locatePoint,
      isMapLocationEdited,
      isMapCenterEdited,
      countriesDataSource,
      dashboardsDataSource,
      deviceConfigured,

      updateSiteInfo,
      updateOrgInfo,
      openDashFolderClick,
      openDashboardClick,
      openRTMonitorClick,

      tabs,
      tabSelectedIx,
      isUserAdmin,
    }
  },
}
</script>

<style lang="scss">
.home-container {
  max-width: 1350px;
  margin: 0 auto;
}
</style>
